import React, { useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { AlertTriangle, Trash2 } from 'lucide-react'
import { Modal } from './Modal'
import { Button } from './Button'

// ─────────────────────────────────────────────────────────────────────────────
// confirmDialog , promise-based replacement for window.confirm in the v2 look.
//
//   if (await confirmDialog({ title: 'Entwurf löschen?', danger: true })) …
//
// Options:
//   title         required , the question ("Diese Umfrage schließen?")
//   message       optional supporting line under the title
//   confirmLabel  default "Bestätigen" (use a VERB: "Löschen", "Schließen")
//   cancelLabel   default "Abbrechen"
//   danger        red-tinted warning icon (default true — this dialog exists
//                 for destructive actions). The confirm button stays in the
//                 platform's one action color so hierarchy is unmistakable.
//   kicker        small uppercase label, default "Bitte bestätigen"
//
// Mounts its own portal root, resolves true/false, then unmounts. Escape and
// backdrop-click cancel; focus starts on Abbrechen so Enter never destroys
// anything by accident.
// ─────────────────────────────────────────────────────────────────────────────

function ConfirmDialog({ title, message, confirmLabel, cancelLabel, danger = true, kicker, icon, onDone }) {
  const cancelRef = useRef(null)
  useEffect(() => { cancelRef.current?.focus() }, [])
  const Icon = icon || (danger ? Trash2 : AlertTriangle)
  return (
    <Modal open onClose={() => onDone(false)} maxWidth={420}>
      <div className="px-[18px] pt-5 pb-1 flex items-start gap-3.5">
        <div className={'w-10 h-10 rounded-full grid place-items-center flex-shrink-0 ' + (danger ? 'bg-[#fbeae6] text-[#b3321c] shadow-[0_0_0_5px_#fdf4f2]' : 'bg-[#fff1e8] text-[#e85d1f] shadow-[0_0_0_5px_#fff6ef]')}>
          <Icon size={17} />
        </div>
        <div className="min-w-0 pt-0.5">
          <span className="block text-[10.5px] font-bold tracking-[0.10em] uppercase text-[#a8a49c] mb-0.5">
            {kicker || 'Bitte bestätigen'}
          </span>
          <h2 className="m-0 text-[15.5px] font-semibold tracking-tight text-[#1a1816] leading-snug">{title}</h2>
          {message ? <p className="m-0 mt-1.5 text-[12.5px] text-[#6b6862] leading-relaxed">{message}</p> : null}
        </div>
      </div>
      <div className="flex items-center gap-2 px-[18px] py-3.5 mt-3 border-t border-[#f0eee9]">
        <span className="flex-1" />
        <Button ref={cancelRef} variant="ghost" onClick={() => onDone(false)}>{cancelLabel || 'Abbrechen'}</Button>
        <Button variant="primary" onClick={() => onDone(true)}>{confirmLabel || 'Bestätigen'}</Button>
      </div>
    </Modal>
  )
}

export function confirmDialog(options) {
  return new Promise(resolve => {
    const host = document.createElement('div')
    document.body.appendChild(host)
    const root = createRoot(host)
    const done = result => {
      root.unmount()
      host.remove()
      resolve(result)
    }
    root.render(<ConfirmDialog {...options} onDone={done} />)
  })
}
